/// <amd-module name="Build/Dashboard/BuildHistogramWidget" />
import * as React from "react";

import { Build, BuildResult, BuildStatus, BuildQueryOrder, DefinitionQueueStatus } from "Build/Client/Contracts/Build";
import { getDurationStats, padZeroes } from "Build/Common/Library/Duration";
import { Status as IndicatorStatus, getStatusIndicatorData } from "Build/Common/Library/Indicator";
import { BuildSource } from "Build/Common/Library/Sources/Build";
import { IGetBuildsOptions } from "Build/Common/Library/Sources/Build.types";
import { BuildHistogramAriaLabel, BuildHistogramNoBuildsLabel } from "Build/Dashboard/Resources";
import { Histogram, HistogramStates } from "Build/Shared/Components/Histogram/Histogram";
import { IHistogramItem } from "Build/Shared/Components/Histogram/Histogram.types";

import { Components, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { Ago } from "VSSUI/Ago";
import { IStatusProps, Status, StatusSize } from "VSSUI/Status";

import { ago } from "VSSUI/Utilities/Date";
import { format } from "VSS/Core/Util/String";

import * as Resources from "Build/Dashboard/Resources";

const widgetWidth = 302;
const barPadding = 2;

export interface IBuildHistogramWidgetProps {
    definitionId: number;
    projectId: string;
    itemsCount: number;
    fullBranchName?: string;
    type?: number;
  }

  export interface IBuildHistogramWidgetState {
    builds: Build[];
}

export class BuildHistogramWidgetDomClasses {
    public static container: string = "build-histogram-widget";
}

export class BuildHistogramWidget extends VssComponent<IBuildHistogramWidgetProps, IBuildHistogramWidgetState, IVssComponentContext> {

    private _buildSource: BuildSource; 

    constructor(props: IBuildHistogramWidgetProps, context: IVssComponentContext) {
        super(props, context);
        this.state = { builds: [] };

        this._buildSource = new BuildSource({
            pageContext: this.context.pageContext,
            project: this.props.projectId
        })
    }

    public render() {
        if (this.state.builds.length === 0) {
            return <div className={BuildHistogramWidgetDomClasses.container}>
                <span className="no-builds-label">{BuildHistogramNoBuildsLabel}</span>
            </div>;
        }

        const latestBuild = this.state.builds[this.state.builds.length - 1];
        const latestBuildIndicatorData = getIndicatorData(latestBuild);
        const count = this.props.itemsCount;

        let histogramItems = this.state.builds.map(build => this._buildToHistogramItem(build));
        // pad with empty items so the latest build always appears in the rightmost spot
        if (histogramItems.length < count) {
            const paddingItemsCount = count - histogramItems.length;
            const paddingItems: IHistogramItem[] = [];
            for (let i = 0; i < paddingItemsCount; i++) {
                paddingItems[i] = { id: -1 * i };
            }
            histogramItems = paddingItems.concat(histogramItems);
        }
        // Scale the bar width based on the number of items
        const barWidth = count > 0 ? (widgetWidth / count) - barPadding : undefined;
        return <div className={BuildHistogramWidgetDomClasses.container}>
            <Histogram
                items={histogramItems}
                ariaLabelForList={BuildHistogramAriaLabel}
                width={barWidth}
            />
            <div className="build-histogram-footer">
                <Status {...latestBuildIndicatorData.statusProps} size={StatusSize.m} className="status-icon" />
                <Ago date={latestBuild.finishTime} />
            </div>
        </div>;
    }

    componentDidMount() {
        this._fetchBuilds();
    }

    componentDidUpdate(prevProps: IBuildHistogramWidgetProps) {
        if (this.props.definitionId !== prevProps.definitionId
            || this.props.projectId !== prevProps.projectId
            || this.props.fullBranchName !== prevProps.fullBranchName
            || this.props.itemsCount > prevProps.itemsCount
            || this.props.type !== prevProps.type)  {
            this._fetchBuilds();
        } else if (this.props.itemsCount < prevProps.itemsCount) {
            this.setState({ builds: this.state.builds.slice(0, this.props.itemsCount) })
        }
    }

    private _fetchBuilds() {
        const queryOptions = {
            project: this.props.projectId,
            definitions: [this.props.definitionId],
            statusFilter: BuildStatus.Completed,
            queryOrder: BuildQueryOrder.FinishTimeDescending,
            top: this.props.itemsCount            
        } as IGetBuildsOptions;

        if (this.props.fullBranchName) {
            queryOptions.branchName = this.props.fullBranchName;
        }
      
        if (this.props.type) {
            queryOptions.type = this.props.type;
        }
        if(this.props.type == 1) {
            this._buildSource.getXAMLBuilds(queryOptions).then(builds => {
                builds.reverse();
                this.setState({ builds });
            }).catch(err => {
                console.log(err);
            });
        }
        else {
        this._buildSource.getBuilds(queryOptions).then(builds => {
            builds.reverse();
            this.setState({ builds });
        }).catch(err => {
            console.log(err);
        });}
    }  

    private _buildToHistogramItem(build: Build): IHistogramItem {
        let state = HistogramStates.Unknown;
        if (build.result) {
            switch (build.result) {
                case BuildResult.Succeeded:
                    state = HistogramStates.Success;
                    break;
                case BuildResult.Failed:
                case BuildResult.Canceled:
                    state = HistogramStates.Failure;
                    break;
                case BuildResult.PartiallySucceeded:
                    state = HistogramStates.Warning;
                    break;
            }
        }

        let value = 0;
        if (state !== HistogramStates.Unknown) {
            const currentTime = Date.now();
            const startTime = build.startTime ? build.startTime.getTime() : (build.queueTime ? build.queueTime.getTime() : currentTime);
            const finishTime = build.finishTime ? build.finishTime.getTime() : currentTime;
            value = finishTime - startTime;
        }

        const indicatorData = getIndicatorData(build);

        return {
            id: build.id,
            href: build._links["web"].href,
            tooltip: indicatorData.tooltip,
            value,
            state
        } as IHistogramItem
    }
}

enum IndicatorSize {
    Large,
    ExtraLarge
}

interface IOriginalStatusInfo {
    text: string;
}

interface IFinalStatusInfo {
    text: string;
    duration: string;
}

interface IIndicatorData {
    statusProps: IStatusProps;
    className: string;
    text: string;
    tooltip: string;
    originalStatusInfo: IOriginalStatusInfo;
    finalStatusInfo: IFinalStatusInfo;
}

function getDurationText(milliseconds: number): string {
    const durationStats = getDurationStats(milliseconds);
    let text = "";
    if (durationStats.days > 0) {
        text = format("{0}:{1}:{2}:{3}",
            padZeroes(durationStats.days),
            padZeroes(durationStats.hours),
            padZeroes(durationStats.minutes),
            padZeroes(durationStats.seconds));
    }
    else if (durationStats.hours > 0) {
        text = format("{0}:{1}:{2}",
            padZeroes(durationStats.hours),
            padZeroes(durationStats.minutes),
            padZeroes(durationStats.seconds));
    }
    else if (durationStats.minutes > 0) {
        text = format("{0}:{1}",
            padZeroes(durationStats.minutes),
            padZeroes(durationStats.seconds));
    }
    else if (durationStats.seconds > 0) {
        text = format(Resources.SecondsFormat, padZeroes(durationStats.seconds));
    }
    else {
        text = format(Resources.MillisecondsFormat, padZeroes(milliseconds));
    }

    return text;
}

function getIndicatorData(build: Build | null, size: IndicatorSize = IndicatorSize.Large): IIndicatorData {
    let iconClassName = "";
    switch (size) {
        case IndicatorSize.Large:
            iconClassName = "ci-large-icon";
            break;
        case IndicatorSize.ExtraLarge:
            iconClassName = "ci-extra-large-icon";
            break;
    }

    let statusData = getStatusIndicatorData(IndicatorStatus.Unknown);
    let info = {
        statusProps: statusData.statusProps,
        text: Resources.Unknown,
        className: iconClassName,
        tooltip: "",
        reason: "",
        originalStatusInfo: { text: "", localeDate: "", localeTime: "" },
        finalStatusInfo: { text: "", duration: "" }
    } as IIndicatorData;

    if (!build) {
        return info;
    }

    info.originalStatusInfo.text = Resources.Started;
    info.finalStatusInfo.text = Resources.RanFor;

    switch (build.status) {
        case BuildStatus.NotStarted:
            statusData = getStatusIndicatorData(IndicatorStatus.Queued);
            if (build.definition.queueStatus === DefinitionQueueStatus.Paused) {
                info.text = Resources.BuildDefinitionIsPaused;
            }
            else {
                info.text = Resources.NotStarted;
            }

            info.originalStatusInfo.text = Resources.Queued;
            info.finalStatusInfo.text = Resources.QueuedFor;
            break;
        case BuildStatus.InProgress:
            statusData = getStatusIndicatorData(IndicatorStatus.InProgress);
            info.text = Resources.Running;
            info.finalStatusInfo.text = Resources.RunningFor;
            break;
        case BuildStatus.Cancelling:
            statusData = getStatusIndicatorData(IndicatorStatus.InProgress);
            info.text = Resources.Canceling;
            info.finalStatusInfo.text = Resources.RunningFor;
            break;
        case BuildStatus.Completed:
            if (build.startTime) {
                const milliseconds = Math.abs(build.finishTime.getTime() - build.startTime.getTime());
                info.finalStatusInfo.duration = getDurationText(milliseconds);
            }

            switch (build.result) {
                case BuildResult.Succeeded:
                    statusData = getStatusIndicatorData(IndicatorStatus.Succeeded);
                    info.text = Resources.Succeeded;
                    break;
                case BuildResult.PartiallySucceeded:
                    statusData = getStatusIndicatorData(IndicatorStatus.Warning);
                    info.text = Resources.SucceededWithIssues;
                    break;
                case BuildResult.Failed:
                    statusData = getStatusIndicatorData(IndicatorStatus.Failed);
                    info.text = Resources.Failed;
                    break;
                case BuildResult.Canceled:
                    statusData = getStatusIndicatorData(IndicatorStatus.Cancelled);
                    info.text = Resources.Canceled;
                    break;
            }
    }

    info.statusProps = statusData.statusProps;
    const ranForText = info.finalStatusInfo.duration ? "(" + format(Resources.RanForFormat, info.finalStatusInfo.duration) + ")" : "";
    info.tooltip = format(Resources.BuildStatusIndicatorFormat, build.buildNumber, info.text.toLocaleLowerCase(), ago(build.finishTime ? build.finishTime : build.startTime ? build.startTime : new Date()).toLocaleLowerCase(), ranForText.toLocaleLowerCase());

    return info;
}

Components.add("build-dashboard-histogram", BuildHistogramWidget);